/**
 * Shared formatting utilities — use these everywhere.
 * All dates → IST, DD MMM YYYY
 * All prices → Indian locale ₹
 */

const IST_LOCALE = 'en-IN';
const IST_TZ = 'Asia/Kolkata';

/**
 * Format a date/datetime string or Date object to IST: "07 Apr 2026"
 * Returns '—' for null/undefined/invalid.
 */
export function formatDate(val) {
  if (val == null || val === '') return '—';
  try {
    const d = typeof val === 'string' ? new Date(val) : val;
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(IST_LOCALE, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: IST_TZ,
    });
  } catch {
    return '—';
  }
}

/**
 * Format a date/datetime string or Date object to IST with time: "07 Apr 2026, 09:15"
 * Returns '—' for null/undefined/invalid.
 */
export function formatDateTime(val) {
  if (val == null || val === '') return '—';
  try {
    const d = typeof val === 'string' ? new Date(val) : val;
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString(IST_LOCALE, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: IST_TZ,
    });
  } catch {
    return '—';
  }
}

/**
 * Format a number as Indian-locale currency: "₹1,23,456.78"
 * Returns '—' for null/undefined.
 */
export function formatPrice(val, decimals = 2) {
  if (val == null) return '—';
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num)) return '—';
  return new Intl.NumberFormat(IST_LOCALE, {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
}

/**
 * Format a plain number with Indian grouping (no ₹ symbol): "1,23,456.78"
 */
export function formatNumber(val, decimals = 2) {
  if (val == null) return '—';
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num)) return '—';
  return new Intl.NumberFormat(IST_LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
}

/**
 * Format P&L with sign prefix: "+₹1,234.56" or "-₹1,234.56"
 */
export function formatPnL(val, decimals = 2) {
  if (val == null) return '—';
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num)) return '—';
  const prefix = num >= 0 ? '+' : '';
  return prefix + formatPrice(num, decimals);
}
